import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    CircularProgress,
    Container,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import * as lodash from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as DrainPipeHelper from '../../api/drainPipe/drainPipeHelper';
import { EStatusCode } from '../../api/enum';
import { IResponse } from '../../api/httpClient';
import { diameter, drainPipeDiameterPD, internalDiameter } from '../../conversion/drainPipeDiameter';
import { route } from '../../route/Index';
import DropDown from '../../shared/component/DropDown';
import Input from '../../shared/component/Input';
import { EStatus, EUnitOfMeasure } from '../../shared/enum';
import isAdmin from '../../shared/hoc/isAdmin';
import { IDrainPipe, IDrainPipeDiameter } from '../../shared/iProject';
import variable from '../../shared/style/variable';
import { toastAlert } from '../../shared/util';
import { useAppSelector } from '../../store/hook';
import { selectProject } from '../../store/project/projectSlice';
import { LoadingButton } from '@mui/lab';

interface IDrainPipeDetailProps {}

const DrainPipeContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

let newPhysicalDataMetric: IDrainPipeDiameter = {
    unitOfMeasure: EUnitOfMeasure.Metric,
    diameter: 0,
    internalDiameter: 0,
};

let newPhysicalDataImperial: IDrainPipeDiameter = {
    unitOfMeasure: EUnitOfMeasure.Imperial,
    diameter: 0,
    internalDiameter: 0,
};

let newDrainPipe: IDrainPipe = {
    isBase: true,
    status: EStatus.Test,
    name: 'New drain pipe',
    countries: ['Italy'],
    drainPipeDiameters: [newPhysicalDataMetric, newPhysicalDataImperial],
};

const DrainPipeDetail = (props: IDrainPipeDetailProps) => {
    let { param } = useParams();
    const project = useAppSelector(selectProject);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [drainPipe, setDrainPipe] = React.useState<IDrainPipe | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaveLoading, setIsSaveLoading] = React.useState(false);
    const [unitOfMeasure, setUnitOfMeasure] = React.useState<EUnitOfMeasure>(EUnitOfMeasure.Metric);

    React.useEffect(() => {
        const fetchDrainPipe = async () => {
            setIsLoading(true);
            if (param === 'newDrainPipe') {
                setDrainPipe(newDrainPipe);
            } else {
                let drainPipeId = parseInt(param!);
                let drainPipe = await DrainPipeHelper.getDrainPipeByIdAsync(drainPipeId);
                setDrainPipe(drainPipe.data);
            }
            setIsLoading(false);
        };
        fetchDrainPipe();
    }, []);

    const handleClickSave = async () => {
        let payload: IDrainPipe = {
            ...drainPipe!,
        };
        let res: IResponse<IDrainPipe>;
        setIsSaveLoading(true)
        if (param === 'newDrainPipe') {
            res = await DrainPipeHelper.insertDrainPipeAsync(payload);
        } else {
            res = await DrainPipeHelper.updateDrainPipeAsync(payload);
        }
        if (res.status == EStatusCode.Ok) {
            toastAlert('success', t('savedDrainPipe'));
        }
        setIsSaveLoading(false)
    };

    const handleChangeGeneral = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let t = lodash.cloneDeep(drainPipe);
        t = {
            ...t,
            [name]: value,
        };
        setDrainPipe(t);
    };

    const handleChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, id: number) => {
        let { name, value } = event.target;
        let t = lodash.cloneDeep(drainPipe);
        let index = t!.drainPipeDiameters?.findIndex(x => x.id == id)!;
        t!.drainPipeDiameters![index] = {
            ...t!.drainPipeDiameters![index],
            [name]: parseFloat(value),
        };
        setDrainPipe(t);
    };

    return (
        <>
            <Stack direction={'row'} padding={'0 1rem'} alignItems={'center'}>
                <Link to={route.backoffice} style={{ textDecoration: 'none' }}>
                    <Stack alignItems={'center'} direction={'row'}>
                        <ArrowBackIosIcon style={{ fill: variable.greenLight }} />
                        <Typography variant='h6' padding={'1rem 0'} color={variable.greenLight}>
                            {t('back')}
                        </Typography>
                    </Stack>
                </Link>
                <Stack width={'100%'} textAlign={'center'}>
                    <Typography variant='h6' color={variable.greenLight}>
                        {param == 'newDrainPipe' ? t('addNewDrainPipe') : t('updateDrainPipe')}
                    </Typography>
                </Stack>
            </Stack>
            {isLoading && <CircularProgress />}
            {!isLoading && drainPipe && (
                <DrainPipeContainer maxWidth={'md'}>
                    <Stack height={'95%'} direction='column'>
                        <Stack padding={'0 1rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Stack flexDirection={'row'}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={drainPipe?.status == EStatus.Public}
                                            onChange={e => setDrainPipe({ ...drainPipe!, status: e.target.checked ? EStatus.Public : EStatus.Test })}
                                        />
                                    }
                                    label={t('public')}
                                />
                                <Stack flexDirection={'row'} gap={1}>
                                    <InputLabel style={{ alignContent: 'center', color: 'black' }}>{t('unitOfMeasure')}</InputLabel>
                                    <DropDown value={unitOfMeasure} onChange={e => setUnitOfMeasure(e.target.value as any)}>
                                        <MenuItem value={EUnitOfMeasure.Metric}>{t('metric')}</MenuItem>
                                        <MenuItem value={EUnitOfMeasure.Imperial}>{t('imperial')}</MenuItem>
                                    </DropDown>
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack height={'100%'} direction={'row'} spacing={1}>
                            <Stack width={'50%'} position='relative'>
                                <Stack variant='scrollable'>
                                    <Accordion disableGutters>
                                        <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                            <Typography>{t('general')}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Input id={'name'} label={t('name')} name={'name'} value={drainPipe.name} onChange={(e: any) => handleChangeGeneral(e)} />
                                        </AccordionDetails>
                                    </Accordion>
                                </Stack>
                            </Stack>
                            <Stack width={'50%'} position='relative'>
                                <Stack variant='scrollable'>
                                    {drainPipe
                                        .drainPipeDiameters!.filter(x => x.unitOfMeasure == unitOfMeasure)
                                        .map((x, i) => (
                                            <Accordion disableGutters>
                                                <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                    <Typography>{t(`data ${i}`)}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Input
                                                        id={diameter}
                                                        label={t(diameter, {
                                                            unitOfMeasure: drainPipeDiameterPD[diameter]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={diameter}
                                                        value={x.diameter}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={internalDiameter}
                                                        label={t(internalDiameter, {
                                                            unitOfMeasure: drainPipeDiameterPD[internalDiameter]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={internalDiameter}
                                                        value={x.internalDiameter}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    {drainPipe.drainPipeDiameters!.length > 1 && (
                                                        <IconButton
                                                            style={{ justifyContent: 'end', width: '100%' }}
                                                            aria-label='remove-data-drainPipe'
                                                            onClick={() => setDrainPipe({ ...drainPipe, drainPipeDiameters: drainPipe.drainPipeDiameters!.filter((y: any, u: any) => u !== i) })}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    <IconButton
                                        style={{ justifyContent: 'end' }}
                                        aria-label='remove-image-material'
                                        onClick={() =>
                                            setDrainPipe({
                                                ...drainPipe,
                                                drainPipeDiameters: [
                                                    ...drainPipe.drainPipeDiameters!,
                                                    unitOfMeasure == EUnitOfMeasure.Metric ? { ...newPhysicalDataMetric } : { ...newPhysicalDataImperial },
                                                ],
                                            })
                                        }>
                                        <AddIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack alignItems='end'>
                            <Stack direction={'row'} spacing={2}>
                                <Button variant='white' onClick={() => navigate(route.backoffice)}>
                                    {t('back')}
                                </Button>
                                <LoadingButton loading={isSaveLoading} style={{ overflow: 'hidden' }} variant='orange' onClick={handleClickSave}>
                                    {t('save')}
                                </LoadingButton>
                            </Stack>
                        </Stack>
                    </Stack>
                </DrainPipeContainer>
            )}
        </>
    );
};

export default isAdmin(DrainPipeDetail);
